import React from 'react';
import {
  Button,
  Input,
  Text,
  Flex,
  Spinner,
  useDisclosure,
} from '@chakra-ui/react';
import { Popover } from '../../Popover';
import { Upload01 } from '@kula-ai/icons-core';
import { ModalWithForm } from '../../modal';
import { getEsignVoidBlockedText, getEsignReplacementText } from './constants';

export const useReplacementUploader = ({
  handleFileChange,
  replacedData,
  isReplacing,
  atsOffer,
}) => {
  const fileUploadRef = React.useRef<HTMLInputElement>(null);

  const confirmationModalActions = useDisclosure();

  const { offer_status, esign_envelope } = atsOffer || {};

  const nonVoidableStatus = [
    'rejected_by_candidate',
    'rejected_by_company_signer',
    'accepted_by_candidate',
  ].includes(offer_status);

  const renderReplacementContent = () => {
    if (isReplacing) {
      return (
        <Flex
          minH="100px"
          flexShrink={0}
          alignItems="center"
          justifyContent="center"
        >
          <Spinner color="primary-800" />
        </Flex>
      );
    }

    if (replacedData?.status === 'verified') {
      return (
        <Flex flexDir="column" gap="10px">
          <Text fontSize="sm">
            <Text as="span" size="sm-medium">
              Verified:
            </Text>{' '}
            Document verified, good to go.
          </Text>
          <Flex flexWrap="wrap" gap="5px">
            {Object.entries(replacedData.tokens)?.map(([key, value]) => {
              if (value === 'valid')
                return (
                  <Text
                    key={key}
                    bg="white"
                    p="4px 8px"
                    borderRadius="sm"
                    color="ats-text-tertiary"
                    border="1px solid #DBDBDB"
                  >{`{{${key}}}`}</Text>
                );
              return null;
            })}
          </Flex>
        </Flex>
      );
    }

    if (replacedData?.status === 'error') {
      return (
        <Flex flexDir="column" gap="10px">
          <Text fontSize="sm">
            <Text as="span" size="sm-medium">
              Warning:
            </Text>{' '}
            the uploaded file contains one or more invalid tokens. Please fix
            the tokens and re-upload before moving forward! The following tokens
            need to be updated:
          </Text>
          <Flex flexWrap="wrap" gap="5px">
            {Object.entries(replacedData.tokens)?.map(([key, value]) => {
              if (value === 'invalid')
                return (
                  <Text
                    key={key}
                    bg="white"
                    p="4px 8px"
                    borderRadius="sm"
                    color="ats-text-tertiary"
                    border="1px solid #DBDBDB"
                  >{`{{${key}}}`}</Text>
                );
              return null;
            })}
          </Flex>
        </Flex>
      );
    }

    return null;
  };

  const handleFileReplacement = () => {
    if (esign_envelope?.id) {
      confirmationModalActions.onOpen();
    } else {
      fileUploadRef.current?.click();
    }
  };

  const confirmReplacement = () => {
    confirmationModalActions.onClose();
    setTimeout(() => {
      fileUploadRef.current?.click();
    }, 0);
  };
  let headerText = '';
  let subHeaderText = undefined;

  const envelopeKind = esign_envelope?.kind;

  const providerConfig = {
    docusign: { name: 'Docusign', item: 'envelope' },
    pandadocs: { name: 'PandaDoc', item: 'document' },
    adobe: { name: 'Adobe Sign', item: 'agreement' },
  };

  const providerKey = envelopeKind || null;
  const provider = providerKey ? providerConfig[providerKey] : null;

  if (esign_envelope?.id && provider) {
    if (nonVoidableStatus) {
      headerText = `Upload replacement: Unable to void the ${provider.name} ${provider.item}`;
      subHeaderText = (
        <Flex gap="14px" flexDir="column">
          <Text size="sm-medium" color="ats-text-secondary">
            {getEsignVoidBlockedText(provider.name, provider.item)}
          </Text>
          <Text size="sm-medium" color="ats-text-secondary">
            {getEsignReplacementText(provider.name, provider.item)}
          </Text>
        </Flex>
      );
    } else {
      headerText = `Note: This action will void the ${provider.name} ${provider.item}`;
      subHeaderText = `Uploading a replacement will void the existing ${provider.name} ${provider.item}. You can create a new ${provider.item} for this candidate after uploading.`;
    }
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileChange(e);
  };

  const ReplacementUploader = ({ isLoading }) => (
    <>
      <Popover
        popoverProps={{ trigger: 'hover' }}
        triggerElement={
          <>
            <Button
              variant="quaternary"
              isDisabled={isLoading}
              onClick={handleFileReplacement}
            >
              <Upload01
                size="1em"
                className="kula-icon"
                style={{ marginRight: '5px' }}
              />
              Upload replacement
            </Button>
            <Input
              type="file"
              ref={fileUploadRef}
              accept=".docx"
              onChange={handleFileInputChange}
              onClick={(e) => {
                (e.target as HTMLInputElement).value = '';
              }}
              style={{ display: 'none' }}
            />
          </>
        }
      >
        <>
          <Text size="sm-medium" color="ats-text-primary">
            Make sure all the tokens are right
          </Text>
          <Text fontSize="10px" fontWeight="400" color="ats-text-secondary">
            Double check which tokens you have in your doc.
          </Text>
        </>
      </Popover>
      {/* Modal for confirmation */}
      {confirmationModalActions?.isOpen && (
        <ModalWithForm
          withForm={false}
          isOpen={confirmationModalActions?.isOpen}
          onClose={confirmationModalActions?.onClose}
          headerText={headerText}
          subHeaderText={subHeaderText}
          primaryCtaText="Continue to upload replacement"
          handleSubmit={confirmReplacement}
        />
      )}
    </>
  );

  return {
    ReplacementUploader,
    renderReplacementContent,
  };
};
